import { memo, useEffect, useRef, useState, useCallback } from "react"
import onScanJS from "onscan.js"
import OutlinedInput, { IOutlinedInput } from "src/shared/components/outlined-input"
import { GS1Parser, parseGS1StringInterface } from "src/shared/utils/g1-datamatrix.ts/parseGS1DataMatrix"
import { toast } from "react-toastify"

const keyCodeMap: Record<number, string> = {
  189: "_",
  173: "-",
  190: ".",
  188: ",",
  191: "/",
  186: ";",
  222: "'",
  219: "[",
  221: "]",
  220: "\\",
  187: "=",
  192: "~",
  223: "`"
}

const BarcodeScanner = ({
  onScan,
  onScanRaw,
  isAttachedScanner = true,
  placeholder = "Search by Barcode",
  isPipeAllowed = true,
  ...rest
}: {
  onScan?: (barcode: string, possibleBarcodes: string[]) => void
  onScanRaw?: (parsedData: parseGS1StringInterface) => void
  isAttachedScanner?: boolean
  placeholder?: string
  isPipeAllowed?: boolean
} & IOutlinedInput) => {
  const [searchBarcode, setSearchBarcode] = useState("")
  const barcodeSearchRef = useRef<any>(null)

  /**
   * Legacy middleware for backward compatibility
   * Only used when onScanRaw is provided (GRN, Stock Intake)
   */
  const handleScanMiddleware = useCallback((rawBarcode: string) => {
    const barcode = rawBarcode.trim()

    // Handle pipe-separated barcodes (legacy behavior)
    if (barcode.includes("|") && isPipeAllowed) {
      const rawBarcodes = rawBarcode.split("|")
      const candidatesActionIds = []
      for (let a = rawBarcodes.length; a > 0; a--) {
        candidatesActionIds.push(rawBarcodes.slice(0, a).join("|"))
      }
      // Legacy callback signature - not used by new CreateStockTransfer
      if (onScan) {
        // @ts-ignore - Legacy signature support
        onScan(rawBarcode, candidatesActionIds)
      }
      return
    }

    // Show error for pipe-separated without permission
    if (barcode.includes("|")) {
      toast.error(`${barcode} QR code is not in GS1 standard, please turn off manufacturer tracking.`)
      return
    }

    // Parse GS1 (legacy behavior)
    const {
      actionId,
      parsedData,
      actionIdCandidates
    } = GS1Parser.parseAndExtractActionId(barcode)

    // Legacy onScanRaw callback
    if (onScanRaw) {
      onScanRaw(parsedData)
    }

    // Legacy onScan callback with candidates
    if (onScan) {
      // @ts-ignore - Legacy signature support
      onScan(actionId as string, actionIdCandidates)
    }
  }, [onScan, onScanRaw, isPipeAllowed])

  useEffect(() => {
    if (!isAttachedScanner) return

    // Configure onScanJS with optimized settings for rapid scanning
    onScanJS.attachTo(document, {
      keyCodeMapper: (event) => mapKeyCode(event),
      // Aggressive timing to capture rapid scans
      timeBeforeScanTest: 200,  // Faster detection
      avgTimeByChar: 50,         // Faster per-character timing
      minLength: 3,              // Minimum barcode length (prevents false triggers)
      onScan: (scannedCode: any) => {
        // Ignore scans when manual input field is focused
        if (document?.activeElement?.id === "barcode-scan-gen-input") return

        if (scannedCode) {
          const barcode = String(scannedCode).trim()

          // CRITICAL FIX: Validate barcode before processing
          // Prevents calling onScan with empty/invalid strings on initialization
          if (!barcode || barcode.length === 0) {
            console.log("⚠️ Ignoring empty barcode scan")
            return
          }

          // Minimum length validation - most barcodes are at least 3 characters
          if (barcode.length < 3) {
            console.log("⚠️ Ignoring too short barcode:", barcode)
            return
          }

          // NEW BEHAVIOR (non-blocking): If only onScan is provided (CreateStockTransfer)
          // Pass raw barcode immediately with NO processing
          if (onScan && !onScanRaw) {
            console.log("✅ Valid barcode scan:", barcode)
            onScan(barcode, [])
            return
          }

          // LEGACY BEHAVIOR: If onScanRaw is provided (GRN, Stock Intake)
          // Use middleware for backward compatibility
          if (barcode) {
            handleScanMiddleware(barcode)
          }
        }
      },
      onScanButtonLongPress: () => {
        console.log("Scan button long pressed")
      }
    })

    return () => {
      onScanJS.detachFrom(document)
    }
  }, [onScan, onScanRaw, isAttachedScanner, handleScanMiddleware])

  const mapKeyCode = (event: KeyboardEvent): string => {
    const keyCode = event.which || event.keyCode
    if (keyCodeMap[keyCode]) {
      return keyCodeMap[keyCode]
    }
    return onScanJS.decodeKeyEvent(event)
  }

  const handleSearch = (barcode: string) => {
    const trimmed = barcode?.trim()

    // Validate barcode
    if (!trimmed || trimmed.length === 0) {
      console.log("⚠️ Ignoring empty manual input")
      setSearchBarcode("")
      return
    }

    // Minimum length validation
    if (trimmed.length < 3) {
      console.log("⚠️ Barcode too short:", trimmed)
      toast.error("Barcode must be at least 3 characters")
      return
    }

    // NEW BEHAVIOR: Non-blocking mode
    if (onScan && !onScanRaw) {
      onScan(trimmed, [])
    } else {
      // LEGACY BEHAVIOR: Processing mode
      handleScanMiddleware(trimmed)
    }

    setSearchBarcode("")
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    const searchValue = e.clipboardData.getData("text")
    if (!searchValue) return
    setTimeout(() => {
      handleSearch(searchValue)
    }, 200)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return
    handleSearch(searchBarcode)
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    setSearchBarcode(e.target.value)
  }

  return (
    <OutlinedInput
      id="barcode-scan-gen-input"
      size="small"
      placeholder={placeholder}
      value={searchBarcode}
      onChange={handleChange}
      inputRef={barcodeSearchRef}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      sx={{ height: "35px", background: (theme) => theme.palette.secondary.main, ...rest?.sx }}
      fullWidth
      {...rest}
    />
  )
}

export default memo(BarcodeScanner)
